import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Download, FileText, CalendarClock, X, Plus } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { berlinerBetriebswoche, tagUeberschrift } from '../lib/zeit';

const REPORT_TYPES = [
  { id: 'driver_activity', label: 'Fahrer-Aktivität' },
  { id: 'driver_quality', label: 'Fahrer-Qualität' },
  { id: 'driver_status', label: 'Fahrer-Status' },
  { id: 'driver_time_distance', label: 'Fahrer Zeit & Distanz' },
  { id: 'finance_ledger_activity', label: 'Buchungen – Information' },
  { id: 'trip_activity', label: 'Fahrt-Aktivität' },
  { id: 'vehicle_performance', label: 'Fahrzeug-Leistung' },
  { id: 'vehicle_time_distance', label: 'Fahrzeug Zeit & Distanz' },
];

const STORAGE_KEY = 'partner_reports';
const loadStored = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; } };
const saveStored = (list) => localStorage.setItem(STORAGE_KEY, JSON.stringify(list));

function defaultRange() {
  return berlinerBetriebswoche();
}

export default function Reports() {
  const [tab, setTab] = useState('reports'); // 'reports' | 'schedules'
  const [reports, setReports] = useState(loadStored());
  const [schedules, setSchedules] = useState(() => { try { return JSON.parse(localStorage.getItem('partner_schedules') || '[]'); } catch { return []; } });
  const [drawer, setDrawer] = useState(null); // null | 'generate' | 'schedule'
  const [form, setForm] = useState({ type: 'driver_activity', ...defaultRange() });
  const [schedForm, setSchedForm] = useState({ type: 'trip_activity', frequency: 'weekly' });
  const [busy, setBusy] = useState(false);

  useEffect(() => saveStored(reports), [reports]);
  useEffect(() => localStorage.setItem('partner_schedules', JSON.stringify(schedules)), [schedules]);

  const typeLabel = (id) => REPORT_TYPES.find(t => t.id === id)?.label || id;

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.get('/partners/reports/generate', { params: form, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const name = `${form.from.replace(/-/g, '')}-${form.to.replace(/-/g, '')}-${form.type}`;
      const a = document.createElement('a'); a.href = url; a.download = `${name}.csv`; a.click();
      const entry = { id: Date.now(), name, type: form.type, from: form.from, to: form.to, frequency: 'Manuell', generated_on: new Date().toISOString() };
      setReports(r => [entry, ...r]);
      toast.success('Bericht erstellt & heruntergeladen');
      setDrawer(null);
    } catch (err) { toast.error('Fehler beim Erstellen'); }
    finally { setBusy(false); }
  };

  const redownload = async (rep) => {
    try {
      const res = await api.get('/partners/reports/generate', { params: { type: rep.type, from: rep.from, to: rep.to }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `${rep.name}.csv`; a.click();
    } catch { toast.error('Download fehlgeschlagen'); }
  };

  const createSchedule = () => {
    setSchedules(s => [{ id: Date.now(), type: schedForm.type, frequency: schedForm.frequency, created: new Date().toISOString() }, ...s]);
    toast.success('Zeitplan erstellt');
    setDrawer(null);
  };

  // ZEIT-HELFER-001: lokale Werte (JJJJ-MM-TT bzw. ISO), trotzdem EIN Formatweg über lib/zeit.
  const fmtDate = (iso) => tagUeberschrift(iso);
  const freqLabel = { daily: 'Täglich', weekly: 'Wöchentlich', monthly: 'Monatlich' };

  return (
    <div style={{ position: 'relative' }}>
      <PageHeader
        title="Berichte"
        actions={
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setDrawer('schedule')} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: '#f3f4f6', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
            <CalendarClock size={15} /> Neuen Zeitplan erstellen
          </button>
          <button onClick={() => setDrawer('generate')} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: '#000', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
            <FileText size={15} /> Bericht erstellen
          </button>
        </div>
        }
      />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 24, borderBottom: '1px solid #e5e7eb', marginBottom: 18, marginTop: 18 }}>
        {[['reports', 'Berichte'], ['schedules', 'Zeitpläne']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding: '10px 4px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: tab === k ? '#000' : '#6b7280', borderBottom: tab === k ? '2px solid #000' : '2px solid transparent', marginBottom: -1 }}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'reports' && (
        <div style={{ overflowX: 'auto' }}>{/* LESBARKEIT-LAYOUT-001: Tabelle scrollt im Container, nie die Seite */}<table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ textAlign: 'left' }}>
            {['Berichtsname', 'Typ', 'Zeitraum', 'Frequenz', 'Erstellt am', 'Aktionen'].map(h => (
              <th key={h} style={{ padding: '12px 14px', fontSize: 13, color: '#374151', fontWeight: 700 }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {reports.length === 0 && <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Noch keine Berichte erstellt. Klicke auf „Bericht erstellen“.</td></tr>}
            {reports.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                <td style={{ padding: '14px', fontSize: 13, color: '#374151', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</td>
                <td style={{ padding: '14px', fontSize: 13 }}>{typeLabel(r.type)}</td>
                <td style={{ padding: '14px', fontSize: 13, color: '#4b5563' }}>{fmtDate(r.from)} – {fmtDate(r.to)}</td>
                <td style={{ padding: '14px', fontSize: 13, color: '#4b5563' }}>{r.frequency}</td>
                <td style={{ padding: '14px', fontSize: 13, color: '#4b5563' }}>{fmtDate(r.generated_on)}</td>
                <td style={{ padding: '14px' }}>
                  <button onClick={() => redownload(r)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#f3f4f6', borderRadius: 6, fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                    <Download size={13} /> Download
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {tab === 'schedules' && (
        <div style={{ overflowX: 'auto' }}>{/* LESBARKEIT-LAYOUT-001: Tabelle scrollt im Container, nie die Seite */}<table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ textAlign: 'left' }}>
            {['Typ', 'Frequenz', 'Erstellt', 'Aktionen'].map(h => <th key={h} style={{ padding: '12px 14px', fontSize: 13, color: '#374151', fontWeight: 700 }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {schedules.length === 0 && <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Keine Zeitpläne. Erstelle einen automatischen Bericht.</td></tr>}
            {schedules.map(s => (
              <tr key={s.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                <td style={{ padding: '14px', fontSize: 13 }}>{typeLabel(s.type)}</td>
                <td style={{ padding: '14px', fontSize: 13, color: '#4b5563' }}>{freqLabel[s.frequency]}</td>
                <td style={{ padding: '14px', fontSize: 13, color: '#4b5563' }}>{fmtDate(s.created)}</td>
                <td style={{ padding: '14px' }}>
                  <button onClick={() => setSchedules(list => list.filter(x => x.id !== s.id))} style={{ padding: '6px 12px', background: '#fef2f2', color: '#dc2626', borderRadius: 6, fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer' }}>Löschen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {/* Drawer */}
      {drawer && (
        <>
          <div onClick={() => setDrawer(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200 }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '90vw', background: '#fff', zIndex: 201, padding: 28, overflowY: 'auto', boxShadow: '-4px 0 30px rgba(0,0,0,0.1)' }}>
            <button onClick={() => setDrawer(null)} style={{ position: 'absolute', top: 20, right: 20, background: 'transparent', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            <h2 style={{ fontSize: 22, fontWeight: 900, marginBottom: 8 }}>{drawer === 'generate' ? 'Bericht erstellen' : 'Zeitplan erstellen'}</h2>
            <p style={{ fontSize: 14, color: '#4b5563', marginBottom: 24 }}>
              {drawer === 'generate' ? 'Erstelle einen einmaligen Bericht mit den Optionen unten' : 'Berichte automatisch nach Zeitplan erzeugen'}
            </p>

            <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 6 }}>Berichtstyp</label>
            <select value={drawer === 'generate' ? form.type : schedForm.type}
              onChange={e => drawer === 'generate' ? setForm(f => ({ ...f, type: e.target.value })) : setSchedForm(f => ({ ...f, type: e.target.value }))}
              style={{ width: '100%', padding: '12px', border: '1.5px solid #000', borderRadius: 8, fontSize: 14, marginBottom: 20, background: '#fff' }}>
              {REPORT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>

            {drawer === 'generate' ? (
              <>
                <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 6 }}>Von</label>
                <input type="date" value={form.from} onChange={e => setForm(f => ({ ...f, from: e.target.value }))}
                  style={{ width: '100%', padding: '12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, marginBottom: 16, boxSizing: 'border-box' }} />
                <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 6 }}>Bis</label>
                <input type="date" value={form.to} onChange={e => setForm(f => ({ ...f, to: e.target.value }))}
                  style={{ width: '100%', padding: '12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, marginBottom: 28, boxSizing: 'border-box' }} />
                <button onClick={generate} disabled={busy} style={{ width: '100%', padding: '14px', background: '#000', color: '#fff', borderRadius: 8, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                  {busy ? 'Wird erstellt…' : 'Bericht erstellen & herunterladen'}
                </button>
              </>
            ) : (
              <>
                <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 6 }}>Frequenz</label>
                <select value={schedForm.frequency} onChange={e => setSchedForm(f => ({ ...f, frequency: e.target.value }))}
                  style={{ width: '100%', padding: '12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, marginBottom: 28, background: '#fff' }}>
                  <option value="daily">Täglich</option>
                  <option value="weekly">Wöchentlich</option>
                  <option value="monthly">Monatlich</option>
                </select>
                <button onClick={createSchedule} style={{ width: '100%', padding: '14px', background: '#000', color: '#fff', borderRadius: 8, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Plus size={16} /> Zeitplan speichern
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
